import { createApp } from 'vue';
import { createPinia } from 'pinia';
import Antd, { message } from 'ant-design-vue';
import 'ant-design-vue/dist/reset.css';
import App from './App.vue';
import router from './router';

// Design Tokens - MUST be imported before main.css to override defaults
import './assets/tokens/index.css';

import './styles/main.css';

// Configure dayjs with UTC and timezone plugins
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Main entry point for Vue.js application
 */
const app = createApp(App);

// Register Pinia for state management
const pinia = createPinia();
app.use(pinia);

// Register Vue Router
app.use(router);

// Register Ant Design Vue
app.use(Antd);

// Configure global toast behavior
message.config({
  top: '80px', // Below the 64px header + breathing room
  duration: 3, // Default 3 seconds
  maxCount: 3, // Show max 3 toasts at once; queue others
  rtl: false,
});

// Mount app
app.mount('#app');

/**
 * Accessibility: Add ARIA attributes to Ant Design's dynamic regions.
 *
 * Ant Design Vue 4.x does not expose `role` or `aria-live` on its
 * `message` toasts or `notification` toasts by default. Screen readers
 * therefore miss status updates triggered via `message.error()` /
 * `message.success()` / `notification.*`. We use a single MutationObserver
 * to add the appropriate attributes the moment these containers mount.
 *
 * - `.ant-message-notice`     → role="status"  + aria-live="polite"
 *   (covers success/info/warning toasts which are non-interrupting)
 * - `.ant-notification-notice` → role="alert"   + aria-live="assertive"
 *   (notifications are more prominent and should announce immediately)
 *
 * The observer is idempotent — it skips elements that already have a role.
 */
const setupAntdA11y = (): void => {
  if (typeof MutationObserver === 'undefined') return;

  const observer = new MutationObserver(() => {
    document.querySelectorAll<HTMLElement>('.ant-message-notice').forEach((el) => {
      if (!el.getAttribute('role')) {
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-atomic', 'true');
      }
    });
    document.querySelectorAll<HTMLElement>('.ant-notification-notice').forEach((el) => {
      if (!el.getAttribute('role')) {
        el.setAttribute('role', 'alert');
        el.setAttribute('aria-live', 'assertive');
        el.setAttribute('aria-atomic', 'true');
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

setupAntdA11y();